import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  MarginalPrices,
  MarginalPricesRequestBody,
  MarginalPricesSlim,
} from './types';

export const marginalPricesApi = createApi({
  reducerPath: 'marginalPricesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/marginal-prices`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['MarginalPrices', 'MarginalPricesList'],
  endpoints: builder => ({
    getMarginalPricesI: builder.infiniteQuery<
      MarginalPricesSlim[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['MarginalPrices', 'MarginalPricesList'],
    }),
    getMarginalPricesById: builder.query<MarginalPrices, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['MarginalPrices'],
    }),
    createMarginalPrices: builder.mutation<
      MarginalPrices,
      MarginalPricesRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'MarginalPricesList',
        { type: 'MarginalPrices', id: result?.id },
      ],
    }),
    updateMarginalPrices: builder.mutation<
      MarginalPrices,
      MarginalPricesRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'MarginalPricesList',
        { type: 'MarginalPrices', id: result?.id },
      ],
    }),
  }),
});

export const {
  useGetMarginalPricesByIdQuery,
  useCreateMarginalPricesMutation,
  useUpdateMarginalPricesMutation,
} = marginalPricesApi;

export const useGetMarginalPricesIInfiniteQuery = normalizeInfiniteQueryData(
  marginalPricesApi.useGetMarginalPricesIInfiniteQuery,
);
